import {
  ChangeDetectorRef, Component, OnInit, ViewChild, ElementRef, inject, TemplateRef, AfterViewInit
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { DynamicColumnConfig, DynamicTableConfig, DynamicTableFilter, ExportEvent } from 'src/app/shared/dynamic-table/dynamic-table.model';
import { DynamicTableComponent } from 'src/app/shared/dynamic-table/dynamic-table.component';
import { SelectItem } from 'primeng/api';
import { AuthStore } from 'src/app/store/authorization/auth.store';
import { AdminService } from 'src/services/admin.service';
import { Router, ActivatedRoute } from '@angular/router';
import { groupMemberDetails } from 'src/app/model/participant.model';
import { AppConfig } from 'src/app/app.config';
import { SHARED_IMPORTS } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-group-member',
  templateUrl: './group-member.component.html',
  styleUrls: ['./group-member.component.css'],
  standalone: true,
  imports: [...SHARED_IMPORTS, DynamicTableComponent]
})
export class GroupMemberComponent implements OnInit, AfterViewInit {
  @ViewChild('scrollTop', { static: true }) scrollTop: ElementRef;
  @ViewChild(DynamicTableComponent) dynamicTable: DynamicTableComponent;

  @ViewChild('nameTemplate', { static: false }) nameTemplate: TemplateRef<any>;
  @ViewChild('dobTemplate', { static: false }) dobTemplate: TemplateRef<any>;
  @ViewChild('emailTemplate', { static: false }) emailTemplate: TemplateRef<any>;
  @ViewChild('idTemplate', { static: false }) idTemplate: TemplateRef<any>;

  allGroupMembers: groupMemberDetails[] = [];
  filteredGroupMemberList: any[] = [];
  selectedMembers: any[] = [];
  isLoading: boolean = false;
  totalRecords: number = 0;

  cityOptions: SelectItem[] = [];
  stateOptions: SelectItem[] = [];
  zipCodeOptions: SelectItem[] = [];
  countryOptions: SelectItem[] = [];

  datePipe = new DatePipe('en-US');
  private readonly authStore = inject(AuthStore);
  sid: number;
  gid: number

  dynamicColumns: DynamicColumnConfig[] = [];
  tableConfig: DynamicTableConfig = {};
  cellTemplates: { [field: string]: TemplateRef<any> } = {};

  constructor(
    private adminService: AdminService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) { }

  private deferUiUpdate(updateFn: () => void): void {
    Promise.resolve().then(() => {
      updateFn();
      this.cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    this.sid = Number(this.route.snapshot.paramMap.get('sid') || this.route.snapshot.paramMap.get('studyId'));

    this.authStore.setIsAdminUser(true);

    this.tableConfig = {
      rows: 50,
      selectable: false,
      selectionMode: 'multiple',
      globalSearch: true,
      exportable: false,
      deletable: false,
      lazy: false,
      dataKey: 'groupMemberId',
      preferenceKey: 'groupMembers',
      supportFilterHierarchy: true,
      title: 'Group Membership',
      columnFilteringEnabled: false,
      addable: true
    };

    this.dynamicColumns = [
      {
        field: 'groupMemberId',
        header: 'Participant ID',
        customTemplate: 'idTemplate',
        width: '130px',
        sortable: true,
        dataType: 'number',
        filterable: true,
        filterType: 'multiselect',
        filterField: 'groupMemberIdDisplay',
        filterOptions: []
      },
      {
        field: 'participantName',
        header: 'Participant Name',
        width: '200px',
        sortable: true,
        customTemplate: 'nameTemplate',
        filterable: true,
        filterType: 'multiselect',
        filterField: 'participantName',
        filterOptions: []
      },
      {
        field: 'dob',
        header: 'DOB',
        width: '130px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'dobDisplay',
        filterOptions: [],
        sortField: 'dob',
        customTemplate: 'dobTemplate',
        dataType: 'date'
      },
      {
        field: 'phone',
        header: 'Phone',
        width: '130px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'phone',
        filterOptions: []
      },
      {
        field: 'emailAddress',
        header: 'Email Address',
        width: '200px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'emailAddress',
        filterOptions: [],
        customTemplate: 'emailTemplate'
      },
      {
        field: 'streetAddress',
        header: 'Street Address',
        width: '200px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'streetAddress',
        filterOptions: []
      },
      {
        field: 'aptSuiteUnit',
        header: 'Apt/Suite/Unit',
        width: '120px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'aptSuiteUnit',
        filterOptions: []
      },
      {
        field: 'city',
        header: 'City',
        width: '150px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'city',
        filterOptions: []
      },
      {
        field: 'state',
        header: 'State',
        width: '100px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'state',
        filterOptions: []
      },
      {
        field: 'zipCode',
        header: 'Zip Code',
        width: '120px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'zipCode',
        filterOptions: []
      },
      {
        field: 'country',
        header: 'Country',
        width: '130px',
        sortable: true,
        filterable: true,
        filterType: 'multiselect',
        filterField: 'country',
        filterOptions: []
      }
    ];

    this.loadGroupMemberData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.nameTemplate) {
        this.cellTemplates['nameTemplate'] = this.nameTemplate;
      }
      if (this.dobTemplate) {
        this.cellTemplates['dobTemplate'] = this.dobTemplate;
      }
      if (this.emailTemplate) {
        this.cellTemplates['emailTemplate'] = this.emailTemplate;
      }

      this.cellTemplates['idTemplate'] = this.idTemplate;

      // Ensure all filter options are checked by default after view init
      this.dynamicTable?.initializeFiltersWithAllSelected();
    });
  }

  loadGroupMemberData(): void {
    this.isLoading = true;
    this.adminService.getAllGroupMemberDetails()
      .subscribe((_allparticipant: groupMemberDetails[]) => {
        const mappedMembers = (_allparticipant || []).map(member => ({
          groupMemberId: member.groupMemberId,
          groupMemberIdDisplay: member.groupMemberId != null
            ? member.groupMemberId.toString().padStart(6, '0')
            : '000000',
          participantName: `${member.firstName || ''} ${member.middleInitial ? member.middleInitial + ' ' : ''}${member.lastName || ''}`.trim(),
          participantId: member.participantId,
          firstName: member.firstName,
          lastName: member.lastName,
          dob: member.dob,
          dobDisplay: member.dob ? this.datePipe.transform(member.dob, 'MM/dd/yyyy') : '',
          phone: member.phone,
          emailAddress: member.emailId,
          streetAddress: member.mailingStreetAddress,
          aptSuiteUnit: member.aptSuiteUnit,
          city: member.city,
          state: member.state,
          zipCode: member.ZipCode,
          country: member.country
        }));

        this.deferUiUpdate(() => {
          this.allGroupMembers = _allparticipant || [];
          this.filteredGroupMemberList = mappedMembers;

          this.dynamicColumns.forEach(col => {
            if (col.field === 'groupMemberId') {
              const uniqueValues = Array.from(
                new Set(this.filteredGroupMemberList.map(item => item.groupMemberIdDisplay).filter(v => v !== undefined && v !== null))
              );
              col.filterOptions = uniqueValues.map(val => ({ label: val, value: val, checked: true }));
            } else if (col.field === 'dob') {
              const uniqueValues = Array.from(
                new Set(this.filteredGroupMemberList.map(item => item.dobDisplay).filter(v => v !== undefined && v !== null && v !== ''))
              );
              col.filterOptions = uniqueValues.map(val => ({ label: val, value: val, checked: true }));
            } else {
              const uniqueValues = Array.from(
                new Set(this.filteredGroupMemberList.map(item => item[col.field]).filter(v => v !== undefined && v !== null))
              );
              col.filterOptions = uniqueValues.map(val => ({ label: val, value: val, checked: true }));
            }
          });

          this.totalRecords = this.filteredGroupMemberList.length;
          this.initializeFilterOptions();

          setTimeout(() => {
            this.dynamicTable?.initializeFiltersWithAllSelected();
          }, 0);
          this.isLoading = false;
        });
      }, (err) => {
        this.deferUiUpdate(() => {
          this.allGroupMembers = [];
          this.filteredGroupMemberList = [];
          this.totalRecords = 0;
          this.isLoading = false;
        });
        console.error('Failed to load group members', err);
      });
  }


  private initializeFilterOptions(): void {
    this.cityOptions = this.getUniqueOptions('city');
    this.stateOptions = this.getUniqueOptions('state');
    this.zipCodeOptions = this.getUniqueOptions('zipCode');
    this.countryOptions = this.getUniqueOptions('country');
    this.updateDynamicColumnFilterOptions('city', this.cityOptions);
    this.updateDynamicColumnFilterOptions('state', this.stateOptions);
    this.updateDynamicColumnFilterOptions('zipCode', this.zipCodeOptions);
    this.updateDynamicColumnFilterOptions('country', this.countryOptions);
  }

  private getUniqueOptions(field: string): SelectItem[] {
    if (field === 'groupMemberId') {
      const unique = Array.from(new Set(this.filteredGroupMemberList.map(x => x.groupMemberIdDisplay).filter(v => v)));
      return unique.map(val => ({ label: val, value: val }));
    }
    if (field === 'dob') {
      const unique = Array.from(new Set(this.filteredGroupMemberList.map(x => x.dobDisplay).filter(v => v)));
      return unique.map(val => ({ label: val, value: val }));
    }
    const unique = Array.from(new Set(this.filteredGroupMemberList.map(x => x[field]).filter(v => v)));
    return unique.map(val => ({ label: val, value: val }));
  }

  onDynamicFilterChange(filterState: DynamicTableFilter): void {
    // The dynamic table handles client-side filtering internally.
    // No need to manually rebuild filteredGroupMemberList here.
  }


  onFilterOptionsRefresh(event: { field: string; currentFilters: any; hierarchy: string[] }): void {
    // Get unique options for any field
    const options = this.getUniqueOptions(event.field);
    this.updateDynamicColumnFilterOptions(event.field, options);
  }

  onDynamicExport(event: ExportEvent): void {
    console.log('Export event:', event);
  }

  onDynamicPageChange(event: any): void {
    if (this.scrollTop && this.scrollTop.nativeElement) {
      this.scrollTop.nativeElement.focus();
    }
  }

  onDynamicSelectionChange(event: any): void {
    this.selectedMembers = event.selectedRows;
  }

  getGroupMemberDetailsUrl(gId: number): string {
    return `${AppConfig.appsettings.navigatorRoot}admin#/adminhome/group-member-details/${gId}`;
  }

  addRecord(gId: number): void {
    if (gId) {
      this.router.navigate(['/adminhome/group-member-details', gId]);
    } else {
      this.router.navigate(['/adminhome/group-member-details', 0]);
    }
  }

  private updateDynamicColumnFilterOptions(field: string, options: SelectItem[]): void {
    const column = this.dynamicColumns.find(col => col.filterField === field || col.field === field);
    if (column) {
      column.filterOptions = options;
      if (this.dynamicTable) {
        this.dynamicTable.updateColumnFilterOptions(field, options);
      }
    }
  }
}
